import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

export default function BarcodePrintModal({ open, onClose, site, part }) {
  const [printers, setPrinters] = useState(["Default"]);
  const [labelNames, setLabelNames] = useState([]);
  const [printerName, setPrinterName] = useState("Default");
  const [labelSize, setLabelSize] = useState("");
  const [qty, setQty] = useState(1);

  const [previewUrl, setPreviewUrl] = useState("");
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      setErr("");
      setStatus("Loading options…");
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/barcode/options?${qs}`);
        if (!r.ok) throw new Error(await r.text());
        const out = await r.json();

        if (!alive) return;
        setPrinters(out?.printers?.length ? out.printers : ["Default"]);
        setLabelNames(out?.label_names || []);

        // defaults
        setPrinterName((out?.printers?.[0]) || "Default");
        setLabelSize((out?.label_names?.[0]) || "");
        setQty(1);
        setStatus("");
      } catch (e) {
        if (!alive) return;
        setErr(String(e.message || e));
        setStatus("");
      }
    })();

    return () => { alive = false; };
  }, [open, site]);

  // refresh preview whenever selection changes
  useEffect(() => {
    if (!open) return;
    if (!labelSize) return;
    if (!part?.part_number) return;

    let alive = true;

    (async () => {
      setErr("");
      setStatus("Building preview…");
      try {
        const r = await apiFetch("/api/barcode/preview", {
          method: "POST",
          body: JSON.stringify({
            site,
            printer_name: printerName,
            label_size: labelSize,
            part_number: part.part_number,
            description: part.description,
            location: part.location,
            shelve: part.shelve,
            row: part.row,
            date: part.date,
          }),
        });
        if (!r.ok) throw new Error(await r.text());
        const out = await r.json();
        if (!alive) return;
        setPreviewUrl(out?.data_url || "");
        setStatus("");
      } catch (e) {
        if (!alive) return;
        setErr(String(e.message || e));
        setStatus("");
      }
    })();

    return () => { alive = false; };
  }, [open, site, printerName, labelSize, part]);

  const canPrint = !!site && !!labelSize && !!part?.part_number;

  async function doPrint() {
    setErr("");
    if (!canPrint) return;

    const n = Math.max(1, Math.min(Number(qty) || 1, 200));

    try {
      const r = await apiFetch("/api/barcode/print", {
        method: "POST",
        body: JSON.stringify({
          site,
          printer_name: printerName,
          label_size: labelSize,
          quantity: n,
          part_number: part.part_number,
          description: part.description,
          location: part.location,
          shelve: part.shelve,
          row: part.row,
          date: part.date,
        }),
      });
      if (!r.ok) throw new Error(await r.text());

      const html = await r.text();
      const w = window.open("", "_blank");
      if (!w) throw new Error("Popup blocked. Allow popups to print.");
      w.document.open();
      w.document.write(html);
      w.document.close();

      onClose?.();
    } catch (e) {
      setErr(String(e.message || e));
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl w-[min(1100px,95vw)] max-h-[90vh] overflow-hidden shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold">Print Barcode Label</div>
          <button className="px-3 py-1 rounded bg-gray-200" onClick={onClose} type="button">
            Close
          </button>
        </div>

        <div className="p-4 overflow-auto max-h-[80vh]">
          {err && <div className="text-red-600 font-semibold mb-3">{err}</div>}
          {status && <div className="text-gray-600 mb-3">{status}</div>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <div className="text-xs text-gray-600 mb-1">Quantity</div>
              <input
                className="border rounded px-2 py-2 w-full"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div>
              <div className="text-xs text-gray-600 mb-1">Printer Profile</div>
              <select
                className="border rounded px-2 py-2 w-full"
                value={printerName}
                onChange={(e) => setPrinterName(e.target.value)}
              >
                {printers.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                (Actual printer is chosen in the print dialog.)
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-600 mb-1">Label Size</div>
              <select
                className="border rounded px-2 py-2 w-full"
                value={labelSize}
                onChange={(e) => setLabelSize(e.target.value)}
              >
                <option value="">Select…</option>
                {labelNames.map((nm) => <option key={nm} value={nm}>{nm}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border rounded p-3">
              <div className="font-semibold mb-2">Part Info</div>
              <div className="text-sm">
                <div><b>Part:</b> <span className="font-mono">{part?.part_number}</span></div>
                <div><b>Description:</b> {part?.description}</div>
                <div><b>Location:</b> {part?.location} | <b>Shelve:</b> {part?.shelve} | <b>Row:</b> {part?.row}</div>
                <div><b>Date:</b> {part?.date}</div>
              </div>
            </div>

            <div className="border rounded p-3">
              <div className="font-semibold mb-2">Preview</div>
              {previewUrl ? (
                <img src={previewUrl} alt="Label preview" className="w-full border rounded" />
              ) : (
                <div className="text-sm text-gray-600">Preview will appear after selections.</div>
              )}
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              type="button"
              onClick={doPrint}
              disabled={!canPrint}
            >
              Print
            </button>
            <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
